import * as React from "react";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Typography,
  Chip,
  Stack,
  styled,
} from "@mui/material";
import { CardBase } from "../../components/global";
import PersonIcon from "@mui/icons-material/Person";
import { useTitle } from "../../hooks/useTitle";
import { useUILabel } from "../../hooks/useUILabel";
import { Template } from "../../types/template";
import { useGetTemplateByIdQuery } from "../../services/template.service";
import { AttachMoney, Edit } from "@mui/icons-material";
import { nl2br } from "../../utils/helper";
import { ContactPhone, EmailOutlined } from "@mui/icons-material";
import MapPreview from "../../components/MapPreview";
import { decode } from "html-entities";

const StyledActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  padding: theme.spacing(1, 2.5),
  fontWeight: 600,
  textTransform: "none",
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 12px rgba(0, 0, 0, 0.3)"
    : "0 4px 12px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 6px 20px rgba(0, 0, 0, 0.4)"
      : "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const ProjectMeta = ({
  icon,
  value,
}: {
  icon: React.ReactNode;
  value: string;
}) => (
  <Button
    variant="text"
    startIcon={icon}
    size="small"
    sx={{
      mr: { xs: 0, sm: 2 },
      mb: { xs: 0.5, sm: 0 },
      textTransform: "unset",
      color: "text.secondary",
      justifyContent: { xs: "flex-start", sm: "center" },
      fontSize: { xs: "0.8rem", sm: "0.875rem" }
    }}
  >
    {value}
  </Button>
);

const TemplateHeader = ({ data }: { data?: Template; }) => {
  const navigate = useNavigate();
  const editTemplateLabel = useUILabel("project.editTemplate");
  const hourlyLabel = useUILabel("project.hourly");
  const fixedRateLabel = useUILabel("project.fixedRate");
  const independentProfessionalsLabel = useUILabel("project.independentProfessionals");

  const { city, state, zip } = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer;
    } else if (!!data?.location) {
      return data?.location;
    } else
      return {
        city: "?",
        state: "?",
        zip: "?",
      };
  }, [data]);

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "flex-start" }}
        spacing={{ xs: 0, md: 0 }}
        sx={{ mb: { xs: 0, md: 0 } }}
      >
        <Stack direction="column" sx={{ mb: { xs: 0, md: 4 } }} flex={1} width="100%">
          <Stack direction="row" flexWrap="wrap">
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                wordBreak: "break-word"
              }}
            >
              {`#${data?.id || ""} ${data?.title || ""}`}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1, sm: 0 }}
            sx={{ mt: 1 }}
          >
            <ProjectMeta
              icon={<AttachMoney />}
              value={data?.type == "hourly" ? hourlyLabel : fixedRateLabel}
            />
            <ProjectMeta
              icon={<PersonIcon />}
              value={`${data?.num_of_talent || "#"} ${independentProfessionalsLabel}`}
            />
          </Stack>
        </Stack>
        <Stack
          direction={"row"}
          sx={{
            mt: { xs: 2.5, md: 2 },
            mb: { xs: 3.5, md: 0 },
            width: { xs: "100%", md: "auto" }
          }}
        >
          <StyledActionButton
            size="large"
            variant="contained"
            color="success"
            onClick={() => navigate(`/works/templates/${data?.id}/edit`)}
            startIcon={<Edit />}
            sx={{
              width: { xs: "100%", md: "auto" },
              py: { xs: 1.5, md: 1 }
            }}
          >
            {editTemplateLabel}
          </StyledActionButton>
        </Stack>
      </Stack>
    </>
  );
};

const ProjectTemplateDetailPage = () => {
  const { templateId } = useParams();
  const scopeOfWorkLabel = useUILabel("project.scopeOfWork");
  const confidentialInformationLabel = useUILabel("project.confidentialInformation");
  const requirementsLabel = useUILabel("project.requirements");
  const skillsLabel = useUILabel("project.skills");
  const certificationsLabel = useUILabel("project.certifications");
  const notRequiredLabel = useUILabel("project.notRequired");
  const additionalSettingsLabel = useUILabel("project.additionalSettings");
  const numberOfIPsLabel = useUILabel("project.numberOfIPs");
  const estimatedAmountOfHoursLabel = useUILabel("project.estimatedAmountOfHours");
  const photosRequiredLabel = useUILabel("project.photosRequired");
  const finalNotesRequiredLabel = useUILabel("project.finalNotesRequired");
  const toolsRequiredLabel = useUILabel("project.toolsRequired");
  const mileRadiusLabel = useUILabel("project.mileRadius");
  const backgroundCheckRequiredLabel = useUILabel("project.backgroundCheckRequired");
  const drugTestRequiredLabel = useUILabel("project.drugTestRequired");
  const workSiteLabel = useUILabel("project.workSite");
  const metadataLabel = useUILabel("project.metadata");
  const createdAtLabel = useUILabel("project.createdAt");
  const lastUpdateLabel = useUILabel("project.lastUpdate");

  const { data, isLoading, isFetching } = useGetTemplateByIdQuery(
    parseInt(templateId!),
    {
      skip: !templateId,
    }
  );

  useTitle(`Template #${data?.id}`, `${data?.title} | Template`);

  const [lng, lat] = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer?.coordinate?.coordinates || [];
    } else if (!!data?.location) {
      return data?.location?.coordinate?.coordinates || [];
    } else return [];
  }, [data]);

  return (
    <Box
      sx={{
        pb: { xs: 6, md: 4 },
      }}
    >
      <TemplateHeader data={data} />
      {(isLoading || isFetching) && <LinearProgress />}
      <Grid container spacing={{ xs: 1, md: 2 }} sx={{ mb: 1, mt: { xs: 1, md: 0 } }}>
        <Grid item xs={12} md={8}>
          <CardBase
            title={scopeOfWorkLabel}
            useGlassCard={true}
            nested={false}
          >
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.8,
                wordBreak: "break-word",
                fontSize: { xs: "0.875rem", sm: "0.9375rem" }
              }}
            >
              {nl2br(decode(data?.description))}
            </Typography>
          </CardBase>
          <CardBase
            title={confidentialInformationLabel}
            useGlassCard={true}
            nested={false}
            sx={{ mt: { xs: 1.5, md: 2 } }}
          >
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.8,
                wordBreak: "break-word",
                fontSize: { xs: "0.875rem", sm: "0.9375rem" }
              }}
            >
              {nl2br(data?.confidential_information) || "n/a"}
            </Typography>
          </CardBase>
          {/* <CardBase
            title="Tasks"
            useGlassCard={true}
            nested={true}
            sx={{ mt: 2 }}
          >
            {data?.tasks?.length == 0 && (
              <Typography variant="caption" color="text.secondary">
                No tasks specified
              </Typography>
              )}
              {data?.tasks?.map((it, idx) => (
                <Accordion key={idx}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>{it.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="caption">
                      {decode(it.description) || "n/a"}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
          </CardBase> */}
          <CardBase
            title={requirementsLabel}
            useGlassCard={true}
            nested={false}
            sx={{ mt: { xs: 1.5, md: 2 } }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
                {skillsLabel}
              </Typography>
              {data?.skills?.length == 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {notRequiredLabel}
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                  {data?.skills?.map((it) => (
                    <Chip
                      label={it.name}
                      key={it.id}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(18, 144, 60, 0.1)",
                        color: "#12903C",
                        border: "1px solid rgba(18, 144, 60, 0.2)",
                      }}
                    />
                  ))}
                </Stack>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#12903C", fontWeight: 600 }}>
                {certificationsLabel}
              </Typography>
              {data?.certifications?.length == 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {notRequiredLabel}
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {data?.certifications?.map((it) => (
                    <Chip
                      label={it.name}
                      key={it.id}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(18, 144, 60, 0.1)",
                        color: "#12903C",
                        border: "1px solid rgba(18, 144, 60, 0.2)",
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </CardBase>
          <CardBase
            title={additionalSettingsLabel}
            useGlassCard={true}
            nested={false}
            sx={{ mt: { xs: 1.5, md: 2 } }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {numberOfIPsLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {data?.num_of_talent}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {estimatedAmountOfHoursLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {data?.approximate_hours_to_complete} hour
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {photosRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.is_photo_required ? "Yes" : "No"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {finalNotesRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.is_final_notes_required ? "Yes" : "No"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {toolsRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.tool_required ? "Yes" : "No"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {mileRadiusLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {data?.mile_radius} miles
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {backgroundCheckRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.background_check_required ? "Yes" : "No"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} display="inline">
                  {drugTestRequiredLabel}:{" "}
                </Typography>
                <Typography variant="body2" display="inline">
                  {!!data?.drug_test_required ? "Yes" : "No"}
                </Typography>
              </Box>
            </Stack>
          </CardBase>
        </Grid>
        <Grid item xs={12} md={4}>
          <CardBase
            title={workSiteLabel}
            useGlassCard={true}
            nested={false}
          >
            <Box
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                mb: 2,
                height: { xs: 200, sm: 250, md: 300 },
                border: (theme) =>
                  `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
              }}
            >
              <MapPreview lat={lat} lng={lng} />
            </Box>
            {!!data?.location && (
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1, color: "#12903C" }}>
                  {data?.location?.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    wordBreak: "break-word",
                    fontSize: { xs: "0.85rem", sm: "0.875rem" }
                  }}
                >
                  {data?.location.address_1}, {data?.location.city},{" "}
                  {data?.location.state} {data?.location.zip}
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <ContactPhone fontSize={"small"} color={"disabled"} sx={{ mt: { xs: 0.5, sm: 0 } }} />
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: "break-word",
                      fontSize: { xs: "0.85rem", sm: "0.875rem" }
                    }}
                  >
                    {data?.location?.primary_contact_name} -{" "}
                    {data?.location?.primary_contact_phone_number}
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <EmailOutlined fontSize={"small"} color={"disabled"} sx={{ mt: { xs: 0.5, sm: 0 } }} />
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: "break-all",
                      fontSize: { xs: "0.85rem", sm: "0.875rem" }
                    }}
                  >
                    {data?.location?.primary_contact_email}
                  </Typography>
                </Stack>
              </Box>
            )}
            {!!data?.customer && (
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1, color: "#12903C" }}>
                  {data?.customer?.first_name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    wordBreak: "break-word",
                    fontSize: { xs: "0.85rem", sm: "0.875rem" }
                  }}
                >
                  {data?.customer.physical_address_1}, {data?.customer.city},{" "}
                  {data?.customer.state} {data?.customer.zip}
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <ContactPhone fontSize={"small"} color={"disabled"} sx={{ mt: { xs: 0.5, sm: 0 } }} />
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: "break-word",
                      fontSize: { xs: "0.85rem", sm: "0.875rem" }
                    }}
                  >
                    {data?.customer?.phone_number}
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <EmailOutlined fontSize={"small"} color={"disabled"} sx={{ mt: { xs: 0.5, sm: 0 } }} />
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: "break-all",
                      fontSize: { xs: "0.85rem", sm: "0.875rem" }
                    }}
                  >
                    {data?.customer?.email}
                  </Typography>
                </Stack>
              </Box>
            )}
          </CardBase>
          <CardBase
            title={metadataLabel}
            useGlassCard={true}
            nested={false}
            sx={{
              mt: { xs: 1.5, md: 2 },
              mb: { xs: 2, md: 0 },
            }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  display="block"
                  sx={{
                    mb: 0.75,
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" }
                  }}
                >
                  {createdAtLabel}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    wordBreak: "break-word"
                  }}
                >
                  {dayjs(data?.created_at).format("MMM DD, YYYY hh:mm A z")}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  display="block"
                  sx={{
                    mb: 0.75,
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" }
                  }}
                >
                  {lastUpdateLabel}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    wordBreak: "break-word"
                  }}
                >
                  {dayjs(data?.updated_at).format("MMM DD, YYYY hh:mm A z")}
                </Typography>
              </Box>
            </Stack>
          </CardBase>
        </Grid>
      </Grid>
    </Box>
  );
};
export default ProjectTemplateDetailPage;
